import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import { toast } from "sonner";
import { Trash2, MessageSquare, Heart, FileText, RefreshCw } from "lucide-react";

export default function CommunityModeration() {
  const [data, setData] = useState({ posts: [], comments: [] });
  const [tab, setTab] = useState("posts");

  const load = async () => {
    try { const { data: d } = await api.get("/admin/community/overview"); setData(d); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);
  useAutoRefresh(load);

  const removePost = async (p) => {
    if (!window.confirm(`Delete ${p.author}'s post and all its comments?`)) return;
    try { await api.delete(`/community/posts/${p.id}`); toast.success("Post removed"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeComment = async (c) => {
    if (!window.confirm(`Delete ${c.author}'s comment?`)) return;
    try { await api.delete(`/community/comments/${c.id}`); toast.success("Comment removed"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

  return (
    <div data-testid="community-moderation">
      <p className="text-sm text-zinc-500 mb-4">Monitor everything shared on the Community feed. Delete anything inappropriate — removing a post also removes its comments. Members are not notified.</p>
      <div className="flex items-center gap-2 mb-4">
        {[["posts", `Posts (${data.posts.length})`, FileText], ["comments", `Comments (${data.comments.length})`, MessageSquare]].map(([k, label, Icon]) => (
          <button key={k} data-testid={`mod-tab-${k}`} onClick={() => setTab(k)}
            className={`flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold uppercase tracking-widest border transition-colors ${tab === k ? "bg-pb-red text-black border-pb-red" : "border-pb-border text-zinc-400 hover:text-white"}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
        <button data-testid="mod-refresh" onClick={load} title="Refresh"
          className="ml-auto w-8 h-8 flex items-center justify-center border border-pb-border text-zinc-500 hover:text-pb-cyan hover:border-pb-cyan transition-colors">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="max-h-96 overflow-y-auto divide-y divide-pb-border/50 border border-pb-border bg-pb-base/30" data-testid={`mod-list-${tab}`}>
        {tab === "posts" ? (
          data.posts.length === 0 ? <div className="p-5 text-sm text-zinc-600">No posts yet.</div> :
          data.posts.map((p) => (
            <div key={p.id} data-testid={`mod-post-${p.id}`} className="flex items-start gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-zinc-300">{p.author} <span className="text-zinc-600 font-normal">· {when(p.created_at)}</span></div>
                <div className="text-sm text-zinc-400 line-clamp-2 mt-0.5">{p.content || (p.has_media ? "(media post)" : "")}</div>
                <div className="flex items-center gap-3 text-[10px] text-zinc-600 mt-1">
                  <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{p.likes}</span>
                  <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{p.comment_count}</span>
                  {p.has_media && <span className="uppercase tracking-widest text-pb-cyan">media</span>}
                </div>
              </div>
              <button data-testid={`mod-delete-post-${p.id}`} onClick={() => removePost(p)} title="Delete post"
                className="text-zinc-600 hover:text-red-400 transition-colors shrink-0 mt-1"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))
        ) : (
          data.comments.length === 0 ? <div className="p-5 text-sm text-zinc-600">No comments yet.</div> :
          data.comments.map((c) => (
            <div key={c.id} data-testid={`mod-comment-${c.id}`} className="flex items-start gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-zinc-300">{c.author} <span className="text-zinc-600 font-normal">· {when(c.created_at)}</span></div>
                <div className="text-sm text-zinc-400 line-clamp-2 mt-0.5">{c.content}</div>
                <div className="text-[10px] text-zinc-600 mt-1">on {c.post_author ? `${c.post_author}'s post` : "a post"}: “{c.post_excerpt}”</div>
              </div>
              <button data-testid={`mod-delete-comment-${c.id}`} onClick={() => removeComment(c)} title="Delete comment"
                className="text-zinc-600 hover:text-red-400 transition-colors shrink-0 mt-1"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
